import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { INSPECTION_FIELDS } from "@/graphql/fragments/inspectionFragments";
import { gql, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { Download, Eye, FileText, Loader2 } from "lucide-react";
import PropTypes from 'prop-types';
import { useState } from 'react';
import { toast } from 'sonner';

const GET_INSPECTION_REPORTS = gql`
   ${INSPECTION_FIELDS}
   query GetInspectionsByPermit($permitId: ID!) {
      getInspectionsByPermit(permitId: $permitId) {
         ...InspectionFields
      }
   }
`;

// const GET_INSPECTION_REPORTS = gql`
//    query GetInspectionsByPermit($permitId: ID!) {
//       getInspectionsByPermit(permitId: $permitId) {
//          id
//          inspectionStatus
//          scheduledDate
//          scheduledTime
//          applicationType
//          findings {
//             result
//             observations
//             recommendations
//             attachments {
//                filename
//                contentType
//                data
//             }
//             covFindings {
//                kind
//                species
//                scientificName
//                numberOfTrees
//                quantity {
//                   value
//                   unit
//                }
//                volume {
//                   cubicMeters
//                   boardFeet
//                }
//                conveyance
//                consignee
//             }
//             pltpFindings {
//                treeInventory {
//                   species
//                   numberOfTreesInventoried
//                   grossVolume
//                   netVolume
//                }
//                totalTrees
//                totalGrossVolume
//                totalNetVolume
//             }
//             pltcpFindings {
//                treeInventory {
//                   species
//                   numberOfTreesInventoried
//                   grossVolume
//                   netVolume
//                   treeCategory
//                }
//                totalTrees
//                totalGrossVolume
//                totalNetVolume
//             }
//             tcebpFindings {
//                treeInventory {
//                   species
//                   numberOfTreesInventoried
//                   status
//                   treeCategory
//                }
//                totalTrees
//             }
//          }
//       }
//    }
// `;

const TS_InspectionReportsViewModal = ({ isOpen, onClose, permitId }) => {
   const [previewFile, setPreviewFile] = useState(null);
   // console.log('PermitId in modal:', permitId);

   const { loading, error, data } = useQuery(GET_INSPECTION_REPORTS, {
      variables: { permitId },
      skip: !permitId || !isOpen,
   });

   // console.log('Inspection reports data:', data);

   const handleDownload = (file) => {
      try {
         const byteCharacters = atob(file.data);
         const byteNumbers = new Array(byteCharacters.length);
         for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
         }
         const byteArray = new Uint8Array(byteNumbers);
         const blob = new Blob([byteArray], { type: file.contentType });

         const link = document.createElement('a');
         link.href = URL.createObjectURL(blob);
         link.download = file.filename;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
      } catch (error) {
         console.error('Download error:', error);
         toast.error('Failed to download file');
      }
   };

   const handlePreview = (file) => {
      try {
         const byteCharacters = atob(file.data);
         const byteNumbers = new Array(byteCharacters.length);
         for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
         }
         const byteArray = new Uint8Array(byteNumbers);
         const blob = new Blob([byteArray], { type: file.contentType });
         const fileUrl = URL.createObjectURL(blob);

         // For images
         if (file.contentType.startsWith('image/')) {
            setPreviewFile({
               url: fileUrl,
               name: file.filename,
               type: 'image'
            });
         }
         // For PDFs
         else if (file.contentType === 'application/pdf') {
            setPreviewFile({
               url: fileUrl,
               name: file.filename,
               type: 'pdf'
            });
         }
         // For other file types
         else {
            toast.error('Preview not supported for this file type. Downloading instead...');
            handleDownload(file);
         }
      } catch (error) {
         console.error('Preview error:', error);
         toast.error('Failed to preview file');
      }
   };

   const renderInspectionFindings = (inspection) => {
      switch (inspection.applicationType) {
         case 'Private Tree Plantation Registration':
            return (
               <>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Result</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.result || 'No result recorded'}</p>
                  </div>

                  {inspection.findings?.ptprFindings && (
                     <div className="mt-4">
                        <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">Tree Plantation Location Details</h4>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                           <div className="grid grid-cols-2 gap-4">
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">OCT/TCT No.</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.octTctNumber}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Lot Number</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.lotNumber}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Barangay</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.barangay}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Municipality</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.municipality}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Province</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.province}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Total Lot Area</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.totalLotArea} hectares</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Area Devoted to Plantation</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.areaDevotedToPlantation} hectares</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Spacing</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.spacing}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Tree Species Planted</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.ptprFindings.treeSpeciesPlanted}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Date Planted</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">
                                    {format(new Date(inspection.findings.ptprFindings.datePlanted), 'MMMM yyyy')}
                                 </p>
                              </div>
                           </div>
                        </div>
                     </div>
                  )}

                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Observations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.observations || 'No observations recorded'}</p>
                  </div>

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Recommendations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.recommendations || 'No recommendations recorded'}</p>
                  </div>
               </>
            );

         case 'Certificate of Verification':
            return (
               <>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Result</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.result || 'No result recorded'}</p>
                  </div>

                  {inspection.findings?.covFindings && (
                     <div className="mt-4">
                        <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">Forest Products Information</h4>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                           <div className="grid grid-cols-2 gap-4">
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Kind</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.covFindings.kind}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Species</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.covFindings.species}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Scientific Name</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.covFindings.scientificName}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Number of Trees</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.covFindings.numberOfTrees}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Quantity</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">
                                    {inspection.findings.covFindings.quantity.value} {inspection.findings.covFindings.quantity.unit}
                                 </p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Volume</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">
                                    {inspection.findings.covFindings.volume.cubicMeters} cu.m ({inspection.findings.covFindings.volume.boardFeet} bd ft)
                                 </p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Conveyance</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.covFindings.conveyance}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Consignee</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.covFindings.consignee}</p>
                              </div>
                           </div>
                        </div>
                     </div>
                  )}

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Observations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.observations || 'No observations recorded'}</p>
                  </div>

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Recommendations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.recommendations || 'No recommendations recorded'}</p>
                  </div>
               </>
            );

         case 'Private Land Timber Permit':
            return (
               <>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Result</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.result || 'No result recorded'}</p>
                  </div>

                  {inspection.findings?.pltpFindings && (
                     <div className="mt-4">
                        <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">Property Details</h4>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700 mb-4">
                           <div className="grid grid-cols-2 gap-4">
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Lot Number</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.pltpFindings.lotNumber}</p>
                              </div>
                              <div>
                                 <p className="text-sm text-gray-600 dark:text-gray-400">Original Certificate of Title No.</p>
                                 <p className="font-medium text-gray-900 dark:text-gray-200">{inspection.findings.pltpFindings.octNumber}</p>
                              </div>
                           </div>
                        </div>

                        <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">Tree Inventory</h4>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700 overflow-x-auto">
                           <table className="min-w-full">
                              <thead>
                                 <tr>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Species</th>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Scientific Name</th>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Tree Status</th>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">No. of Trees</th>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Gross Volume (cu.m)</th>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Net Volume (cu.m)</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {inspection.findings.pltpFindings.treeInventory.map((tree, index) => (
                                    <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.species}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.scientificName}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.treeStatus}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.numberOfTreesInventoried}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.grossVolume.toFixed(3)}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.netVolume.toFixed(3)}</td>
                                    </tr>
                                 ))}
                                 <tr className="border-t border-gray-200 font-semibold dark:border-gray-700">
                                    <td colSpan="3" className="py-2 text-gray-900 dark:text-gray-100">Total</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.pltpFindings.totalTrees}</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.pltpFindings.totalGrossVolume.toFixed(3)}</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.pltpFindings.totalNetVolume.toFixed(3)}</td>
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     </div>
                  )}

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Observations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.observations || 'No observations recorded'}</p>
                  </div>

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Recommendations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.recommendations || 'No recommendations recorded'}</p>
                  </div>
               </>
            );

         case 'Public Land Tree Cutting Permit':
            return (
               <>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Result</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.result || 'No result recorded'}</p>
                  </div>

                  {inspection.findings?.pltcpFindings && (
                     <div className="mt-4">
                        <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">Tree Inventory</h4>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700 overflow-x-auto">
                           <table className="min-w-full">
                              <thead>
                                 <tr className="text-left text-gray-500 text-sm dark:text-gray-400">
                                    <th className="pb-2 dark:text-gray-400">Species</th>
                                    <th className="pb-2 dark:text-gray-400">Scientific Name</th>
                                    <th className="pb-2 dark:text-gray-400">No. of Trees</th>
                                    <th className="pb-2 dark:text-gray-400">Gross Volume (cu.m)</th>
                                    <th className="pb-2 dark:text-gray-400">Net Volume (cu.m)</th>
                                    <th className="pb-2 dark:text-gray-400">Category</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {inspection.findings.pltcpFindings.treeInventory.map((tree, index) => (
                                    <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.species}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100 italic">{tree.scientificName || 'N/A'}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.numberOfTreesInventoried}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.grossVolume.toFixed(3)}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.netVolume.toFixed(3)}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.treeCategory}</td>
                                    </tr>
                                 ))}
                                 <tr className="border-t border-gray-200 font-semibold dark:border-gray-700">
                                    <td colSpan="2" className="py-2 text-gray-900 dark:text-gray-100">Total</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.pltcpFindings.totalTrees}</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.pltcpFindings.totalGrossVolume.toFixed(3)}</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.pltcpFindings.totalNetVolume.toFixed(3)}</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100"></td>
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     </div>
                  )}

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Observations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.observations || 'No observations recorded'}</p>
                  </div>

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Recommendations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.recommendations || 'No recommendations recorded'}</p>
                  </div>
               </>
            );

         case 'Tree Cutting and/or Earth Balling Permit':
            return (
               <>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Result</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.result || 'No result recorded'}</p>
                  </div>

                  {inspection.findings?.tcebpFindings && (
                     <div className="mt-4">
                        <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">
                           Trees authorized for {inspection.findings.tcebpFindings.treeInventory?.[0]?.status === 'For Cutting' ? 'Cutting' : 'Earth-balling'}
                        </h4>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700 overflow-x-auto">
                           <table className="min-w-full">
                              <thead>
                                 <tr>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Name of Species</th>
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">No. of Trees</th>
                                    {inspection.findings.tcebpFindings.treeInventory?.[0]?.grossVolume !== undefined ? (
                                       <>
                                          <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Gross Volume (cu.m)</th>
                                          <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Net Volume (cu.m)</th>
                                       </>
                                    ) : (
                                       <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Status</th>
                                    )}
                                    <th className="text-left text-sm font-semibold text-gray-600 pb-2 dark:text-gray-400">Tree Category</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {inspection.findings.tcebpFindings.treeInventory.map((tree, index) => (
                                    <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.species}</td>
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.numberOfTreesInventoried}</td>
                                       {tree.grossVolume !== undefined ? (
                                          <>
                                             <td className="py-2 text-gray-900 dark:text-gray-100">{tree.grossVolume?.toFixed(3) || '0.000'}</td>
                                             <td className="py-2 text-gray-900 dark:text-gray-100">{tree.netVolume?.toFixed(3) || '0.000'}</td>
                                          </>
                                       ) : (
                                          <td className="py-2 text-gray-900 dark:text-gray-100">{tree.status}</td>
                                       )}
                                       <td className="py-2 text-gray-900 dark:text-gray-100">{tree.treeCategory}</td>
                                    </tr>
                                 ))}
                                 <tr className="border-t border-gray-200 font-semibold dark:border-gray-700">
                                    <td className="py-2 text-gray-900 dark:text-gray-100">Total</td>
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.tcebpFindings.totalTrees}</td>
                                    {inspection.findings.tcebpFindings.totalGrossVolume !== undefined ? (
                                       <>
                                          <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.tcebpFindings.totalGrossVolume?.toFixed(3) || '0.000'}</td>
                                          <td className="py-2 text-gray-900 dark:text-gray-100">{inspection.findings.tcebpFindings.totalNetVolume?.toFixed(3) || '0.000'}</td>
                                       </>
                                    ) : (
                                       <td className="py-2 text-gray-900 dark:text-gray-100"></td>
                                    )}
                                    <td className="py-2 text-gray-900 dark:text-gray-100"></td>
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     </div>
                  )}

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Observations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.observations || 'No observations recorded'}</p>
                  </div>

                  <div className="mt-4">
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Recommendations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.recommendations || 'No recommendations recorded'}</p>
                  </div>
               </>
            );

         default:
            return (
               <>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Result</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.result || 'No result recorded'}</p>
                  </div>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Observations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.observations || 'No observations recorded'}</p>
                  </div>
                  <div>
                     <h4 className="font-semibold text-base mb-1 text-gray-900 dark:text-gray-100">Recommendations</h4>
                     <p className="text-gray-600 dark:text-gray-300">{inspection.findings?.recommendations || 'No recommendations recorded'}</p>
                  </div>
               </>
            );
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col bg-white dark:bg-black border dark:border-gray-800">
            <DialogHeader>
               <DialogTitle className="text-gray-900 dark:text-gray-100">Inspection Reports</DialogTitle>
               <DialogDescription className="text-gray-500 dark:text-gray-400">
                  View inspection reports and findings for this application
               </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 h-[calc(90vh-8rem)] overflow-y-auto pr-2">
               {loading ? (
                  <div className="flex items-center justify-center p-4">
                     <Loader2 className="h-6 w-6 animate-spin text-gray-600 dark:text-gray-400" />
                  </div>
               ) : error ? (
                  <div className="text-center text-red-500 dark:text-red-400 p-4">
                     Error loading inspection reports: {error.message}
                  </div>
               ) : data?.getInspectionsByPermit?.length > 0 ? (
                  <div className="space-y-6 p-4">
                     {data.getInspectionsByPermit.map((inspection, index) => (
                        <div key={inspection.id} className="grid gap-5 border dark:border-gray-700 rounded-lg p-4">
                           <div className="flex justify-between items-center">
                              <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                 Inspection Date: {format(new Date(inspection.scheduledDate), 'LLL d, yyyy')}
                              </h3>
                              <Badge variant="outline" className={
                                 inspection.inspectionStatus === 'Completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                                    inspection.inspectionStatus === 'Pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' :
                                       'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
                              }>
                                 {inspection.inspectionStatus}
                              </Badge>
                           </div>

                           <div className="grid gap-4">
                              {renderInspectionFindings(inspection)}
                           </div>

                           {inspection.findings?.attachments?.length > 0 && (
                              <div>
                                 <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">Attachments</h4>
                                 <div className="grid gap-2 border dark:border-gray-700 rounded p-2">
                                    {inspection.findings.attachments.map((file, fileIndex) => (
                                       <div key={fileIndex}
                                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 border dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
                                          <div className="flex items-center gap-2">
                                             <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                             <span className="text-sm text-gray-600 dark:text-gray-300">{file.filename}</span>
                                          </div>
                                          <div className="flex gap-1">
                                             <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handlePreview(file)}
                                                className="hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                             >
                                                <Eye className="h-4 w-4" />
                                             </Button>
                                             <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDownload(file)}
                                                className="hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                             >
                                                <Download className="h-4 w-4" />
                                             </Button>
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           )}
                        </div>
                     ))}
                  </div>
               ) : (
                  <div className="text-center p-4 text-gray-500 dark:text-gray-400">
                     No inspection reports found for this application
                  </div>
               )}
            </ScrollArea>

            {previewFile && (
               <Dialog open={!!previewFile} onOpenChange={() => {
                  setPreviewFile(null);
                  URL.revokeObjectURL(previewFile.url);
               }}>
                  <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-white dark:bg-black border dark:border-gray-800">
                     <DialogHeader className="p-4 border-b dark:border-gray-800">
                        <DialogTitle className="text-gray-900 dark:text-gray-100">
                           {previewFile.name}
                        </DialogTitle>
                     </DialogHeader>
                     <div className="relative flex items-center justify-center p-4 overflow-auto h-full">
                        {previewFile.type === 'image' ? (
                           <img
                              src={previewFile.url}
                              alt={previewFile.name}
                              className="max-w-full max-h-[70vh] object-contain"
                           />
                        ) : previewFile.type === 'pdf' ? (
                           <iframe
                              src={previewFile.url}
                              title={previewFile.name}
                              className="w-full h-[70vh]"
                           />
                        ) : null}
                     </div>
                  </DialogContent>
               </Dialog>
            )}
         </DialogContent>
      </Dialog>
   );
};

TS_InspectionReportsViewModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   permitId: PropTypes.string.isRequired,
};

export default TS_InspectionReportsViewModal;